import { Settings2 } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * PerIntentExpandedBlock — pipeline block whose config splits by intent.
 *
 * Renders the block name + hint in a header strip, then two inline
 * intent sections (signup = amber, signin = green). Each section
 * prints the pinned config as monospace lines with an optional
 * italicised "reason" footer. Read-only in Wave 3.0; the writable
 * config form ships with W3.FE.2.
 *
 * Source of truth: `mockups/unified-pipeline.html` `.block-expanded`.
 */
export interface IntentConfig {
  /** Config lines rendered top-to-bottom under the section label. */
  lines: string[]
  /** Optional italicised "// reason" line shown beneath the lines. */
  reason?: string
}

export interface PerIntentExpandedBlockProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Block name (e.g. `mfa_enroll`). */
  name: string
  /** Hint sub-text shown beside the name (e.g. semantics / "stateful"). */
  hint?: string
  /** Icon glyph in the block header. Defaults to a generic config icon. */
  icon?: React.ReactNode
  /** Config rendered in the amber `signup config` section. */
  signupConfig: IntentConfig
  /** Config rendered in the green `signin config` section. */
  signinConfig: IntentConfig
}

const HEAD_GRADIENT = 'linear-gradient(90deg, rgba(20, 184, 166, 0.04) 0%, transparent 100%)'

interface SectionProps {
  intent: 'signup' | 'signin'
  label: string
  config: IntentConfig
}

function IntentSection({ intent, label, config }: SectionProps) {
  const accent =
    intent === 'signup' ? 'var(--pe-accent-newuser)' : 'var(--pe-accent-existing)'
  const tint =
    intent === 'signup'
      ? 'linear-gradient(90deg, rgba(245, 158, 11, 0.025) 0%, transparent 100%)'
      : 'linear-gradient(90deg, rgba(16, 185, 129, 0.025) 0%, transparent 100%)'
  const glow =
    intent === 'signup' ? 'rgba(245, 158, 11, 0.45)' : 'rgba(16, 185, 129, 0.45)'

  return (
    <div
      className="px-3 py-2 border-b border-dashed last:border-b-0"
      style={{
        backgroundImage: tint,
        borderColor: 'var(--pe-border-sys)',
      }}
      data-testid="per-intent-section"
      data-intent={intent}
    >
      <div
        className="flex items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-[0.10em] mb-1"
        style={{ color: accent }}
      >
        <span
          aria-hidden
          className="w-[7px] h-[7px] rounded-full"
          style={{ background: accent, boxShadow: `0 0 6px ${glow}` }}
        />
        {label}
      </div>
      <div
        className="pl-[13px] font-mono text-[10.5px] leading-[1.5]"
        style={{ color: 'var(--pe-text-secondary)' }}
      >
        {config.lines.map((line, idx) => (
          <React.Fragment key={`${line}-${idx}`}>
            {line}
            {idx < config.lines.length - 1 && <br />}
          </React.Fragment>
        ))}
        {config.reason && (
          <span
            className="block italic mt-0.5 text-[9.5px]"
            style={{ color: 'var(--pe-text-muted)' }}
            data-testid="per-intent-reason"
          >
            // {config.reason}
          </span>
        )}
      </div>
    </div>
  )
}

export const PerIntentExpandedBlock = React.forwardRef<
  HTMLDivElement,
  PerIntentExpandedBlockProps
>(function PerIntentExpandedBlock(
  { className, name, hint, icon, signupConfig, signinConfig, style, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn('flex flex-col rounded-[6px] overflow-hidden border', className)}
      style={{
        background: 'var(--pe-bg-chip)',
        borderColor: 'rgba(20, 184, 166, 0.22)',
        ...style,
      }}
      data-testid="per-intent-expanded-block"
      {...rest}
    >
      <div
        className="flex items-center gap-2.5 px-2.5 py-2.5 border-b"
        style={{
          background: HEAD_GRADIENT,
          borderColor: 'var(--pe-border-subtle)',
        }}
      >
        <div
          className="w-[26px] h-[26px] shrink-0 flex items-center justify-center rounded-[5px]"
          style={{
            background: 'rgba(20, 184, 166, 0.06)',
            color: 'var(--pe-accent-perintent)',
          }}
          aria-hidden
        >
          {icon ?? <Settings2 size={13} strokeWidth={2} />}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[12.5px] font-medium leading-tight truncate"
            style={{ color: 'var(--pe-text-primary)' }}
          >
            {name}
          </div>
          {hint && (
            <div
              className="font-mono text-[9.5px] leading-tight truncate"
              style={{ color: 'var(--pe-text-muted)' }}
            >
              {hint}
            </div>
          )}
        </div>
      </div>

      <IntentSection intent="signup" label="signup config" config={signupConfig} />
      <IntentSection intent="signin" label="signin config" config={signinConfig} />
    </div>
  )
})
